import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const YHANA_WRAPPER_SELECTOR = '.pl-wrapper.pl-size-a4.pl-layout-cut';
const EXPORT_BUTTON_SELECTOR = '.pl-btn-print';
const PHOTO_IMAGE_SELECTOR = '.yhp-photo-cell > img';
const DRESS_IMAGE_SELECTOR = '.yhp-page--dress-reminders img.yhp-janine-dress-image';
const VENUE_IMAGE_SELECTOR = '.yhp-page--venue-janine .yhp-venue-janine-photo > img';
const SOURCE_CLASS = 'yhp-export-source-image';
const PHOTO_LAYER_CLASS = 'yhp-export-photo-layer';
const DRESS_LAYER_CLASS = 'yhp-export-dress-layer';
const VENUE_LAYER_CLASS = 'yhp-export-venue-layer';
const OUTPUT_FILENAME = 'ehlyianha-faith-gil-debut-invitation-A4-portrait-cut-2up.pdf';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const TARGET_DPI = 300;
const MIN_EXPORT_SCALE = 2.75;
const MAX_EXPORT_SCALE = 3.2;
const JPEG_QUALITY = 1;

function quoteCssUrl(value) {
  return `url("${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`;
}

function waitForImage(image) {
  if (image.complete) {
    if (typeof image.decode === 'function') return image.decode().catch(() => undefined);
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const finish = () => {
      image.removeEventListener('load', finish);
      image.removeEventListener('error', finish);
      resolve();
    };

    image.addEventListener('load', finish, { once: true });
    image.addEventListener('error', finish, { once: true });
  });
}

async function waitForRenderableAssets(root) {
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {
    // Continue with the fonts currently available in the browser.
  }

  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(images.map(waitForImage));
}

function nextPaint() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.setTimeout(resolve, 0);
    });
  });
}

function getPrintQualityScale(sheet) {
  const rect = sheet.getBoundingClientRect();
  const cssWidth = rect.width || sheet.scrollWidth || sheet.clientWidth;

  if (!(cssWidth > 0)) return MIN_EXPORT_SCALE;

  const targetA4WidthPixels = (A4_WIDTH_MM / 25.4) * TARGET_DPI;
  const calculatedScale = targetA4WidthPixels / cssWidth;

  return Math.min(MAX_EXPORT_SCALE, Math.max(MIN_EXPORT_SCALE, calculatedScale));
}

function applyBackgroundFromImage(layer, image) {
  const source = image.currentSrc || image.src;
  if (!source) return;

  const computed = window.getComputedStyle(image);
  layer.style.backgroundImage = quoteCssUrl(source);
  layer.style.backgroundRepeat = 'no-repeat';
  layer.style.backgroundSize = computed.objectFit === 'contain' ? 'contain' : 'cover';
  layer.style.backgroundPosition = computed.objectPosition || '50% 50%';
}

function ensurePhotoLayer(image) {
  const frame = image.parentElement;
  if (!frame) return;

  let layer = Array.from(frame.children).find(
    (child) => child.classList?.contains(PHOTO_LAYER_CLASS),
  );

  if (!layer) {
    layer = document.createElement('span');
    layer.className = PHOTO_LAYER_CLASS;
    layer.setAttribute('aria-hidden', 'true');
    frame.appendChild(layer);
  }

  applyBackgroundFromImage(layer, image);
  image.classList.add(SOURCE_CLASS);
}

function prepareYhanaExportImages(wrapper) {
  wrapper.querySelectorAll(PHOTO_IMAGE_SELECTOR).forEach(ensurePhotoLayer);
}

function collectReplacementMetrics(sheet, selector, includeAlignment = false) {
  const image = sheet.querySelector(selector);
  if (!image) return null;

  const source = image.currentSrc || image.src;
  const rect = image.getBoundingClientRect();
  const computed = window.getComputedStyle(image);

  if (!source || !(rect.width > 0) || !(rect.height > 0)) return null;

  return {
    source,
    width: rect.width,
    height: rect.height,
    marginTop: computed.marginTop,
    marginRight: computed.marginRight,
    marginBottom: computed.marginBottom,
    marginLeft: computed.marginLeft,
    alignSelf: includeAlignment ? computed.alignSelf : undefined,
    objectPosition: computed.objectPosition || '50% 50%',
  };
}

function replaceImageInClone(clonedDocument, selector, metrics, className, options = {}) {
  if (!metrics) return;

  const image = clonedDocument.querySelector(selector);
  if (!image) return;

  const replacement = clonedDocument.createElement('span');
  replacement.className = className;
  replacement.setAttribute('aria-hidden', 'true');
  replacement.style.display = 'block';
  replacement.style.width = `${metrics.width}px`;
  replacement.style.height = `${metrics.height}px`;
  replacement.style.maxWidth = '100%';
  replacement.style.minWidth = '0';
  replacement.style.flex = options.flex || '0 0 auto';
  replacement.style.alignSelf = metrics.alignSelf || 'auto';
  replacement.style.marginTop = metrics.marginTop;
  replacement.style.marginRight = metrics.marginRight;
  replacement.style.marginBottom = metrics.marginBottom;
  replacement.style.marginLeft = metrics.marginLeft;
  replacement.style.backgroundColor = options.backgroundColor || 'transparent';
  replacement.style.backgroundImage = quoteCssUrl(metrics.source);
  replacement.style.backgroundRepeat = 'no-repeat';
  replacement.style.backgroundSize = 'contain';
  replacement.style.backgroundPosition = options.center === true
    ? 'center center'
    : metrics.objectPosition;
  replacement.style.boxSizing = 'border-box';
  replacement.style.opacity = '1';
  replacement.style.visibility = 'visible';

  image.replaceWith(replacement);
}

function neutralizeDarkFramesInClone(clonedDocument) {
  const selector = [
    '.yhp-page--cover > .yhp-frame--dark',
    '.yhp-page--closing > .yhp-frame--dark',
  ].join(', ');

  clonedDocument.querySelectorAll(selector).forEach((frame) => {
    frame.style.setProperty('background', 'transparent', 'important');
    frame.style.setProperty('background-color', 'transparent', 'important');
    frame.style.setProperty('background-image', 'none', 'important');
    frame.style.setProperty('box-shadow', 'none', 'important');
    frame.style.setProperty('backdrop-filter', 'none', 'important');
    frame.style.setProperty('-webkit-backdrop-filter', 'none', 'important');
    frame.style.setProperty('filter', 'none', 'important');
    frame.style.setProperty('mix-blend-mode', 'normal', 'important');
    frame.style.setProperty('opacity', '1', 'important');
  });
}

function replaceZeroSizeCanvases(clonedDocument) {
  clonedDocument.querySelectorAll('canvas').forEach((canvas) => {
    const rect = canvas.getBoundingClientRect();
    const width = canvas.width || Math.ceil(rect.width);
    const height = canvas.height || Math.ceil(rect.height);

    if (width > 0 && height > 0) return;

    const replacement = clonedDocument.createElement('span');
    replacement.setAttribute('aria-hidden', 'true');
    replacement.style.display = 'block';
    replacement.style.width = `${Math.max(1, Math.ceil(rect.width))}px`;
    replacement.style.height = `${Math.max(1, Math.ceil(rect.height))}px`;
    replacement.style.background = 'transparent';
    canvas.replaceWith(replacement);
  });
}

function drawScissorsIcon(context, box) {
  const size = Math.min(box.width, box.height);
  if (!(size > 0)) return;

  const offsetX = box.x + ((box.width - size) / 2);
  const offsetY = box.y + ((box.height - size) / 2);
  const scale = size / 24;

  context.save();
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.translate(offsetX, offsetY);
  context.scale(scale, scale);
  context.strokeStyle = '#777777';
  context.lineWidth = 1.9;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  context.beginPath();
  context.arc(6, 6, 2.25, 0, Math.PI * 2);
  context.stroke();

  context.beginPath();
  context.arc(6, 18, 2.25, 0, Math.PI * 2);
  context.stroke();

  context.beginPath();
  context.moveTo(20, 4);
  context.lineTo(8.1, 15.2);
  context.stroke();

  context.beginPath();
  context.moveTo(8.1, 8.8);
  context.lineTo(20, 20);
  context.stroke();
  context.restore();
}

function drawCutGuideScissors(sheet, canvas) {
  const guide = sheet.querySelector('.pl-cut-guide');
  if (!guide) return;

  const sheetRect = sheet.getBoundingClientRect();
  if (!(sheetRect.width > 0) || !(sheetRect.height > 0)) return;

  const scaleX = canvas.width / sheetRect.width;
  const scaleY = canvas.height / sheetRect.height;
  const context = canvas.getContext('2d');
  if (!context) return;

  const iconElements = Array.from(guide.children).filter(
    (element) => element instanceof HTMLElement && element.tagName === 'SPAN',
  );

  iconElements.forEach((element) => {
    const rect = element.getBoundingClientRect();
    drawScissorsIcon(context, {
      x: (rect.left - sheetRect.left) * scaleX,
      y: (rect.top - sheetRect.top) * scaleY,
      width: rect.width * scaleX,
      height: rect.height * scaleY,
    });
  });
}

function createStatusOverlay(wrapper) {
  const overlay = document.createElement('div');
  overlay.className = 'pl-generating-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Generating PDF');
  overlay.innerHTML = `
    <div class="pl-generating-box">
      <div class="pl-generating-spinner" aria-hidden="true"></div>
      <p class="pl-generating-title">Generating PDF...</p>
      <p class="pl-generating-sub" data-yhana-export-status>Preparing the invitation...</p>
    </div>
  `;
  wrapper.appendChild(overlay);
  return overlay;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
}

async function captureSheet(sheet, dressMetrics, venueMetrics) {
  const exportScale = getPrintQualityScale(sheet);

  return html2canvas(sheet, {
    scale: exportScale,
    useCORS: true,
    allowTaint: false,
    backgroundColor: '#ffffff',
    logging: false,
    imageTimeout: 30000,
    removeContainer: true,
    foreignObjectRendering: false,
    scrollX: 0,
    scrollY: 0,
    windowWidth: Math.max(document.documentElement.clientWidth, Math.ceil(sheet.scrollWidth)),
    windowHeight: Math.max(document.documentElement.clientHeight, Math.ceil(sheet.scrollHeight)),
    ignoreElements: (element) => (
      element.tagName === 'CANVAS'
      && (!(element.width > 0) || !(element.height > 0))
    ),
    onclone: (clonedDocument) => {
      const clonedWrapper = clonedDocument.querySelector(YHANA_WRAPPER_SELECTOR);
      clonedWrapper?.classList.add('pl-capturing', 'pl-pdf-exporting');

      const clonedPages = clonedDocument.querySelector('.pl-pages');
      if (clonedPages) {
        clonedPages.style.transform = 'none';
        clonedPages.style.marginBottom = '';
      }

      replaceImageInClone(
        clonedDocument,
        DRESS_IMAGE_SELECTOR,
        dressMetrics,
        DRESS_LAYER_CLASS,
      );
      replaceImageInClone(
        clonedDocument,
        VENUE_IMAGE_SELECTOR,
        venueMetrics,
        VENUE_LAYER_CLASS,
        { backgroundColor: '#fffaf4', center: true },
      );
      neutralizeDarkFramesInClone(clonedDocument);
      replaceZeroSizeCanvases(clonedDocument);
    },
  });
}

async function exportYhanaPdf(wrapper, button) {
  const pages = wrapper.querySelector('.pl-pages');
  const sheets = Array.from(wrapper.querySelectorAll('.pl-sheet--cut'));

  if (!pages || sheets.length === 0) {
    throw new Error('No printable A4 sheets were found.');
  }

  const dressMetricsBySheet = new Map(
    sheets.map((sheet) => [
      sheet,
      collectReplacementMetrics(sheet, DRESS_IMAGE_SELECTOR, true),
    ]),
  );
  const venueMetricsBySheet = new Map(
    sheets.map((sheet) => [
      sheet,
      collectReplacementMetrics(sheet, VENUE_IMAGE_SELECTOR),
    ]),
  );
  const savedTransform = pages.style.transform;
  const savedMarginBottom = pages.style.marginBottom;
  const savedButtonHtml = button.innerHTML;
  const overlay = createStatusOverlay(wrapper);
  const status = overlay.querySelector('[data-yhana-export-status]');

  button.disabled = true;
  button.dataset.exporting = 'true';
  button.textContent = 'Generating PDF...';
  pages.style.transform = 'none';
  pages.style.marginBottom = '';
  wrapper.classList.add('pl-capturing', 'pl-pdf-exporting');

  try {
    prepareYhanaExportImages(wrapper);
    await waitForRenderableAssets(pages);
    await nextPaint();

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [A4_WIDTH_MM, A4_HEIGHT_MM],
      compress: false,
      precision: 16,
    });

    for (let index = 0; index < sheets.length; index += 1) {
      if (status) {
        status.textContent = `Rendering sheet ${index + 1} of ${sheets.length} at print quality...`;
      }

      await nextPaint();
      const sheet = sheets[index];
      const canvas = await captureSheet(
        sheet,
        dressMetricsBySheet.get(sheet),
        venueMetricsBySheet.get(sheet),
      );

      if (!canvas.width || !canvas.height) {
        throw new Error(`Sheet ${index + 1} could not be rendered.`);
      }

      drawCutGuideScissors(sheet, canvas);

      const imageData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      if (index > 0) pdf.addPage([A4_WIDTH_MM, A4_HEIGHT_MM], 'portrait');
      pdf.addImage(
        imageData,
        'JPEG',
        0,
        0,
        A4_WIDTH_MM,
        A4_HEIGHT_MM,
      );

      // Release every high-resolution sheet immediately to keep the browser responsive.
      canvas.width = 1;
      canvas.height = 1;
      await nextPaint();
    }

    if (status) status.textContent = 'Finishing the high-resolution PDF download...';
    await nextPaint();

    const blob = pdf.output('blob');
    downloadBlob(blob, OUTPUT_FILENAME);

    overlay.querySelector('.pl-generating-title').textContent = 'PDF Saved!';
    if (status) status.textContent = OUTPUT_FILENAME;
    window.setTimeout(() => overlay.remove(), 1600);
  } catch (error) {
    console.error('Yhana PDF export failed:', error);
    overlay.querySelector('.pl-generating-title').textContent = 'PDF generation failed';

    if (status) {
      status.textContent = error instanceof Error
        ? error.message
        : 'The PDF could not be generated.';
    }

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'pl-done-btn';
    closeButton.textContent = 'Close';
    closeButton.addEventListener('click', () => overlay.remove(), { once: true });
    overlay.querySelector('.pl-generating-box')?.appendChild(closeButton);
  } finally {
    wrapper.classList.remove('pl-capturing', 'pl-pdf-exporting');
    pages.style.transform = savedTransform;
    pages.style.marginBottom = savedMarginBottom;
    button.disabled = false;
    button.dataset.exporting = 'false';
    button.innerHTML = savedButtonHtml;
  }
}

if (!window.__ehlyianhaExportImageAspectFixInstalled) {
  window.__ehlyianhaExportImageAspectFixInstalled = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest(EXPORT_BUTTON_SELECTOR);
    if (!button || button.dataset.exporting === 'true') return;

    const wrapper = button.closest(YHANA_WRAPPER_SELECTOR);
    if (!wrapper || !wrapper.querySelector('.yhp-page--cover')) return;

    // Use the memory-safe, print-quality Yhana exporter instead of the shared exporter.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    exportYhanaPdf(wrapper, button);
  }, true);
}
